import React from 'react';
import styled from '@emotion/styled';
import { Plus, ArrowLeft } from 'lucide-react';
import PivotPointCurationCard from './PivotPointCurationCard';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { gradientText } from '../../styles/mixins';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
  color: white;
  position: relative;

  html[data-platform='tizen'] & {
    background: #12121a;
  }
`;

const HeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px 16px;
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  ${gradientText('#4CAF50', '#2196F3', '#4CAF50')}
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #2E7BC4;
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { background: #367ABA; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(46, 123, 196, 0.3); }
`;

const SelectionHint = styled.p`
  margin: 0;
  padding: 0 32px 16px;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.5;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 32px 32px;
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 720px;
  margin: 0 auto;
`;


const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 32px;
  text-align: center;
  color: rgba(255, 255, 255, 0.35);
  gap: 16px;
`;

const EmptyTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 500;
`;

/**
 * PivotPointCurationView - Teams create and select their pivot point.
 * Participant mode: click card selects and continues (via onSelect).
 * Admin mode: multi-select via selectedPivotIds (checkbox or card click toggles);
 * the selected set is what Linssit, Ratkaisut and the report work on.
 */
export default function PivotPointCurationView({
  pivotPoints = [],
  edits = {},
  itemCounts = {},
  selectedPivotId: controlledSelectedId,
  selectedPivotIds,
  instanceId,
  onEdit,
  onDelete,
  onSelect,
  onAdd,
  onBack,
  onRefreshClusterAnalysis,
  clusterRefreshPendingIds = new Set(),
}) {
  const { t } = useTranslation();
  const handleSelect = (pivot) => {
    onSelect?.(pivot);
  };
  const multiSelect = !!selectedPivotIds;
  const selectedCount = multiSelect ? pivotPoints.filter(pp => selectedPivotIds.has(pp.id)).length : 0;

  return (
    <Container>
      <HeaderBar>
        <HeaderLeft>
          {onBack && (
            <BackButton onClick={onBack}>
              <ArrowLeft size={16} />
              {t('common.back')}
            </BackButton>
          )}
          <PageTitle>{t('lenses.curation.title')}</PageTitle>
        </HeaderLeft>
        {onAdd && (
          <AddButton onClick={onAdd}>
            <Plus size={16} />
            {t('lenses.curation.add')}
          </AddButton>
        )}
      </HeaderBar>

      {multiSelect && pivotPoints.length > 0 && (
        <SelectionHint>
          {t('lenses.curation.selectionHint', { selected: selectedCount, total: pivotPoints.length })}
        </SelectionHint>
      )}

      <ScrollArea>
        {pivotPoints.length === 0 ? (
          <EmptyState>
            <EmptyTitle>{t('lenses.curation.empty')}</EmptyTitle>
          </EmptyState>
        ) : (
          <CardList>
            {pivotPoints.map(pp => (
              <PivotPointCurationCard
                key={pp.id}
                pivotPoint={pp}
                edit={edits[pp.id]}
                counts={itemCounts[pp.id]}
                selected={selectedPivotIds ? selectedPivotIds.has(pp.id) : pp.id === controlledSelectedId}
                multiSelect={multiSelect}
                onSelect={onSelect ? handleSelect : undefined}
                onEdit={onEdit}
                onDelete={onDelete}
                onRefreshClusterAnalysis={onRefreshClusterAnalysis}
                clusterRefreshPending={clusterRefreshPendingIds.has(pp.id)}
              />
            ))}
          </CardList>
        )}
      </ScrollArea>

    </Container>
  );
}
